import { HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  ApiClientService,
  IBranchSuperintendentStatusRow,
  ICustomerStats,
  IExpiringSummary,
  ILowStockItem,
  ILowStockSummary,
  INearExpiryBatch,
  IOrderStats,
  IPaginatedResponse,
  IShift,
  IDashboardTopProduct,
  IUserCredentialExpiry,
  TENANT_API
} from '@pharvio/core/shared';

@Injectable({ providedIn: 'root' })
export class TenantDashboardDataService {
  private readonly api = inject(ApiClientService);

  // Order stats — single 14-day call feeds all order/revenue widgets
  getOrderStats(params: HttpParams): Observable<IOrderStats> {
    return this.api.get<IOrderStats>(TENANT_API.ORDERS.STATS, params);
  }

  // #4 — Shifts
  getShifts(params: HttpParams): Observable<IPaginatedResponse<IShift>> {
    return this.api.get<IPaginatedResponse<IShift>>(
      TENANT_API.SHIFTS.BASE,
      params
    );
  }

  // #5 — Low stock (count + top N)
  getLowStock(params: HttpParams): Observable<ILowStockSummary> {
    return this.api
      .get<
        IPaginatedResponse<ILowStockItem>
      >(TENANT_API.INVENTORY.LOW_STOCK, params)
      .pipe(
        map((r) => ({
          totalItems: r.meta.totalItems,
          items: r.data.map((i) => ({
            variantId: i.variantId,
            displayName: i.variant.displayName,
            quantityAvailable: i.quantityAvailable,
            currentReorderPoint: i.currentReorderPoint
          }))
        }))
      );
  }

  // #6 — Expiring soon (count + top N)
  getExpiringSoon(params: HttpParams): Observable<IExpiringSummary> {
    return this.api
      .get<
        IPaginatedResponse<INearExpiryBatch>
      >(TENANT_API.INVENTORY.NEAR_EXPIRY, params)
      .pipe(
        map((r) => ({
          totalItems: r.meta.totalItems,
          items: r.data.map((b) => ({
            variantId: b.variantId,
            displayName: b.variant.displayName,
            batchNumber: b.batchNumber,
            expiryDate: b.expiryDate,
            quantityOnHand: b.quantityOnHand,
            status: b.status
          }))
        }))
      );
  }

  // #7 — Out-of-stock count from inventory stats
  getOutOfStockCount(params: HttpParams): Observable<number> {
    return this.api
      .get<{ outOfStockCount: number }>(TENANT_API.INVENTORY.STATS, params)
      .pipe(map((r) => r.outOfStockCount));
  }

  // #10 — Top products
  getTopProducts(params: HttpParams): Observable<IDashboardTopProduct[]> {
    return this.api.get<IDashboardTopProduct[]>(
      TENANT_API.REPORTS.TOP_PRODUCTS,
      params
    );
  }

  // #11 + #12 — Superintendent status (tenant-wide)
  getSuperintendentStatus(): Observable<IBranchSuperintendentStatusRow[]> {
    return this.api.get<IBranchSuperintendentStatusRow[]>(
      TENANT_API.BRANCHES.SUPERINTENDENT_STATUS
    );
  }

  // #13 — Users with credentials expiring
  getUsersExpiringCredentials(
    params: HttpParams
  ): Observable<IUserCredentialExpiry[]> {
    return this.api.get<IUserCredentialExpiry[]>(
      TENANT_API.USERS.CREDENTIALS_EXPIRING,
      params
    );
  }

  // #14 + #15 — Customer stats (date-scoped)
  getCustomerStats(params: HttpParams): Observable<ICustomerStats> {
    return this.api.get<ICustomerStats>(TENANT_API.CUSTOMERS.STATS, params);
  }
}
